import { API_BASE } from "../config";
// =============================================================================
// File: frontend/src/components/VisionGroceryCapture.jsx
// Three Input Methods: Camera Barcode Scan, Manual UPC Entry, Photo Analysis
// Destinations: Pantry OR Shopping Cart (context-aware)
// CSS: All styles in healist-app-components.css under .vgc-* prefix
// Patent claims exercised: P-002 Claims 1-2,6-8 (barcode/UPC product identification)
// =============================================================================

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useBarcodeScanner } from '../hooks/useBarcodeScanner';
import EdamamAttribution from './EdamamAttribution';

const VisionGroceryCapture = ({
  token,
  onItemsConfirmed,  // (items[], destination) => {} - confirmed items
  onClose,
  fetchWithAuth,     // Optional - for authenticated fetches
  onCartFulfilled,   // () => {} - called when cart is fulfilled (409)
  // Context-aware props
  defaultDestination = null,      // 'pantry' | 'cart' | null
  defaultPantryId = null,         // Pre-selected pantry ID
  defaultCartId = null,           // Pre-selected cart ID
  hideDestinationSelector = false // Hide selector when called from specific context
}) => {
  // ==========================================================================
  // STATE
  // ==========================================================================
  
  // Input method: 'continuous' | 'enter' | 'photo'
  // Default to 'photo' - will be updated after device detection
  const [inputMethod, setInputMethod] = useState('photo');
  
  // Photo state
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  
  // Barcode entry state
  const [barcodeInput, setBarcodeInput] = useState('');
  const [isLookingUp, setIsLookingUp] = useState(false);
  
  // Camera scanner state
  const [isScanning, setIsScanning] = useState(false);
  const [scannerError, setScannerError] = useState('');
  const [lastScannedCode, setLastScannedCode] = useState('');
  
  // Common state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [selectedItems, setSelectedItems] = useState([]);

  // Device detection
  const [isMobile, setIsMobile] = useState(false);
  
  // Destination state
  const [destinationType, setDestinationType] = useState(defaultDestination || 'pantry');
  const [pantries, setPantries] = useState([]);
  const [shoppingCarts, setShoppingCarts] = useState([]);
  const [selectedPantryId, setSelectedPantryId] = useState(defaultPantryId);
  const [selectedCartId, setSelectedCartId] = useState(defaultCartId);
  const [loadingDestinations, setLoadingDestinations] = useState(!hideDestinationSelector);
  
  // Refs
  const fileInputRef = useRef(null);
  const hostElementRef = useRef(null);
  const lastScanTimeRef = useRef(0);
  const scannedBarcodesRef = useRef(new Set());
  const DUPLICATE_INTERVAL_MS = 2500;

  // STRICH SDK hook — single source of truth
  const {
    sdkReady: isInitialized,
    error: sdkHookError,
    startCameraScanning,
    stopCameraScanning: stopCameraScanningHook,
    scanImage: scanImageHook
  } = useBarcodeScanner({ fetchWithAuth: fetchWithAuth || undefined });

  // Propagate hook errors
  useEffect(() => {
    if (sdkHookError) setScannerError(sdkHookError);
  }, [sdkHookError]);

  // ==========================================================================
  // AUTH HELPER
  // ==========================================================================
  
  const authFetch = async (url, options = {}) => {
    const headers = {
      ...options.headers,
      'Authorization': `Bearer ${token}`
    };
    if (fetchWithAuth) {
      return fetchWithAuth(url, { ...options, headers });
    }
    return fetch(url, { ...options, headers });
  };
  
  // ==========================================================================
  // INITIALIZATION
  // ==========================================================================
  
  useEffect(() => {
    if (!hideDestinationSelector) {
      loadDestinations();
    } else {
      setLoadingDestinations(false);
    }

    return () => {
      stopCameraScanningHook();
    };
  }, []);
  
  // Device detection effect
  useEffect(() => {
    const checkMobile = () => {
      const userAgent = navigator.userAgent || navigator.vendor || window.opera;
      const isMobileDevice = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent.toLowerCase());
      const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      const isSmallScreen = window.innerWidth <= 1024;
      setIsMobile(isMobileDevice || (isTouchDevice && isSmallScreen));
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  // Set default input method based on device
  useEffect(() => {
    if (isMobile) {
      setInputMethod('continuous');
    } else {
      setInputMethod('photo');
    }
  }, [isMobile]);

  const loadDestinations = async () => {
    setLoadingDestinations(true);
    try {
      const [pantryRes, cartRes] = await Promise.all([
        authFetch(`${API_BASE}/api/pantry/user/list-all`),
        authFetch(`${API_BASE}/api/shopping-lists`)
    ]);
      
      if (pantryRes.ok) {
        const pantryData = await pantryRes.json();
        const pantryList = pantryData.pantries || [];
        setPantries(pantryList);
        if (!selectedPantryId && pantryList.length > 0) {
          setSelectedPantryId(pantryList[0].id);
        }
      }
      
      if (cartRes.ok) {
        const cartData = await cartRes.json();
        const allCarts = cartData.lists || [];
        const activeCarts = allCarts.filter(cart => 
          cart.status !== 'completed' && cart.status !== 'cancelled'
        );
        setShoppingCarts(activeCarts);
        if (!selectedCartId && activeCarts.length > 0) {
          setSelectedCartId(activeCarts[0].id);
        }
      }
    } catch (err) {
      console.error('Failed to load destinations:', err);
    } finally {
      setLoadingDestinations(false);
    }
  };
  
  // ==========================================================================
  // CAMERA BARCODE SCANNING (via useBarcodeScanner hook)
  // ==========================================================================

  const startScanning = async () => {
    if (!isInitialized) {
      setScannerError('Scanner not ready. Please wait or use manual entry.');
      return;
    }
    setScannerError('');
    setIsScanning(true);
  };

  // Effect to start scanner after DOM renders host element
  useEffect(() => {
    if (!isScanning || !hostElementRef.current || !isInitialized) return;

    const timer = setTimeout(async () => {
      const success = await startCameraScanning({
        selector: '#strich-scanner-host',
        onDetection: async (detections) => {
          for (const detection of detections) {
            await handleBarcodeDetection(detection);
          }
        }
      });

      if (!success) {
        setIsScanning(false);
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [isScanning, isInitialized]);

  const stopScanning = async () => {
    await stopCameraScanningHook();
    setIsScanning(false);
  };
  
  const handleBarcodeDetection = async (detection) => {
    const barcode = detection.data;
    
    // Check if already scanned in this session (prevents loop)
    if (scannedBarcodesRef.current.has(barcode)) {
      return;
    }
    
    // Mark as scanned immediately
    scannedBarcodesRef.current.add(barcode);
    setLastScannedCode(barcode);
    
    // Look up the product
    await lookupAndAddBarcode(barcode);
  };
  
  // ==========================================================================
  // BARCODE LOOKUP (shared by camera scan and manual entry)
  // ==========================================================================
  
  const lookupAndAddBarcode = async (barcode) => {
    setIsLookingUp(true);
    setError('');
    
    try {
      const response = await authFetch(`${API_BASE}/api/products/lookup/upc`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ upc: barcode })
      });
      
      if (!response.ok) {
        if (response.status === 404) {
          setError(`Product not found for UPC: ${barcode}`);
          return;
        }
        throw new Error('Failed to lookup product');
      }
      
      const productData = await response.json();
      const product = productData.product || {};

      const newItem = {
        name: product.label || product.name || 'Unknown Product',
        brand: product.brand || '',
        category: product.category || 'Other',
        quantity: 1,
        unit: 'ea',
        barcode: barcode,
        nutrition: product.nutrition || null,
        health_labels: product.health_labels || [],
        selected: true,
        index: selectedItems.length
      };
      
      setSelectedItems(prev => [...prev, newItem]);
      
    } catch (err) {
      console.error('Barcode lookup error:', err);
      setError(err.message || 'Failed to lookup product');
    } finally {
      setIsLookingUp(false);
    }
  };
  
  const handleManualBarcodeSubmit = async () => {
    const barcode = barcodeInput.trim();
    
    if (!barcode) {
      setError('Please enter a UPC/barcode');
      return;
    }
    
    if (!/^\d+$/.test(barcode)) {
      setError('Please enter a valid numeric UPC/barcode');
      return;
    }
    
    if (selectedItems.some(item => item.barcode === barcode)) {
      setError('This product is already in your list');
      return;
    }
    
    await lookupAndAddBarcode(barcode);
    setBarcodeInput('');
  };
  
  const handleBarcodeKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleManualBarcodeSubmit();
    }
  };
  
  // ==========================================================================
  // PHOTO ANALYSIS
  // ==========================================================================
  
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        setError('Please select an image file (JPG, PNG, etc.)');
        return;
      }
      
      if (file.size > 5 * 1024 * 1024) {
        setError('Image size must be less than 5MB');
        return;
      }
      
      setSelectedImage(file);
      setError('');
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };
  
  const handleDrop = (event) => {
    event.preventDefault();
    const file = event.dataTransfer.files[0];
    if (file && file.type.startsWith('image/')) {
      handleFileSelect({ target: { files: [file] } });
    }
  };
  
  const handleDragOver = (event) => {
    event.preventDefault();
  };
  
  const analyzeImage = async () => {
    if (!selectedImage) {
      setError('Please select an image first');
      return;
    }
    
    if (!isInitialized) {
      setError('Barcode scanner not initialized. Please wait and try again.');
      return;
    }
    
    setIsAnalyzing(true);
    setError('');
    
    try {
      // Use hook's scanImage (delegates to STRICH ImageScanner)
      const detections = await scanImageHook(selectedImage);
      
      if (!detections || detections.length === 0) {
        setError('No barcodes detected in image. Try a clearer photo with visible barcodes.');
        setIsAnalyzing(false);
        return;
      }
      
      console.log(`STRICH detected ${detections.length} barcodes:`, detections);
      
      // Dedupe barcodes (in case future versions return multiple)
      const uniqueBarcodes = [...new Set(detections.map(d => d.data))];
      
      // Look up each barcode via Edamam
      const itemsFound = [];
      const notFoundBarcodes = [];
      
      for (const barcode of uniqueBarcodes) {
        try {
          const response = await authFetch(`${API_BASE}/api/products/lookup/upc`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ upc: barcode })
          });
          
          if (response.ok) {
            const productData = await response.json();
            const product = productData.product || {};
            
            itemsFound.push({
              name: product.label || product.name || 'Unknown Product',
              brand: product.brand || '',
              category: product.category || 'Other',
              quantity: 1,
              unit: 'ea',
              barcode: barcode,
              nutrition: product.nutrition || null,
              health_labels: product.health_labels || [],
              selected: true
            });
          } else {
            notFoundBarcodes.push(barcode);
          }
        } catch (err) {
          console.error(`Failed to lookup barcode ${barcode}:`, err);
          notFoundBarcodes.push(barcode);
        }
      }
      
      // Add found items
      if (itemsFound.length > 0) {
        setSelectedItems(prev => [...prev, ...itemsFound.map((item, idx) => ({
          ...item,
          index: prev.length + idx
        }))]);
        setAnalysisResult({ 
          identified_items: itemsFound, 
          source: 'photo_barcode',
          barcodes_detected: uniqueBarcodes.length,
          barcodes_found: itemsFound.length
        });
      }
      
      // Show message about results
      if (notFoundBarcodes.length > 0 && itemsFound.length > 0) {
        setError(`Found ${itemsFound.length} products. ${notFoundBarcodes.length} barcode(s) not in database.`);
      } else if (itemsFound.length === 0) {
        setError(`Detected ${uniqueBarcodes.length} barcode(s) but none found in product database.`);
      }
      
    } catch (err) {
      console.error('Photo analysis error:', err);
      setError(err.message || 'Failed to analyze photo');
    } finally {
      setIsAnalyzing(false);
    }
  };
  
  const updateItemQuantity = (index, quantity) => {
    setSelectedItems(prev => prev.map((item, idx) => 
      idx === index ? { ...item, quantity: Math.max(1, quantity) } : item
    ));
  };
  
  const updateItemName = (index, name) => {
    setSelectedItems(prev => prev.map((item, idx) => 
      idx === index ? { ...item, name } : item
    ));
  };
  
  const removeItem = (index) => {
    setSelectedItems(prev => prev.filter((_, idx) => idx !== index));
  };
  
  const toggleItemSelection = (index) => {
    setSelectedItems(prev => prev.map((item, idx) => 
      idx === index ? { ...item, selected: !item.selected } : item
    ));
  };
  
  const addManualItem = () => {
    setSelectedItems(prev => [...prev, {
      name: '',
      category: 'Other',
      quantity: 1,
      unit: 'ea',
      selected: true,
      isManual: true,
      index: prev.length
    }]);
    
    if (!analysisResult) {
      setAnalysisResult({ identified_items: [], source: 'manual' });
    }
  };
  
  // ==========================================================================
  // CONFIRM & SUBMIT
  // ==========================================================================
  
  const confirmItems = async () => {
    const itemsToAdd = selectedItems.filter(item => item.selected && item.name.trim());
    
    if (itemsToAdd.length === 0) {
      setError('Please select at least one item');
      return;
    }
    
    const targetPantryId = selectedPantryId || defaultPantryId;
    const targetCartId = selectedCartId || defaultCartId;
    
    if (destinationType === 'pantry' && !targetPantryId) {
      setError('Please select a pantry');
      return;
    }
    if (destinationType === 'cart' && !targetCartId) {
      setError('Please select a shopping cart');
      return;
    }
    
    setIsSubmitting(true);
    setError('');
    
    try {
      const endpoint = destinationType === 'pantry' 
        ? `${API_BASE}/api/vision/confirm-grocery-items`
        : `${API_BASE}/api/vision/confirm-grocery-items-to-shopping`;
      
      const payload = {
        vision_analysis_id: analysisResult?.vision_analysis_id || null,
        items: itemsToAdd.map(item => ({
          name: item.name,
          category: item.category,
          quantity: item.quantity,
          unit: item.unit,
          barcode: item.barcode,
          nutrition: item.nutrition,
          health_labels: item.health_labels
        }))
      };
      
      if (destinationType === 'pantry') {
        payload.target_pantry_id = targetPantryId;
      } else {
        payload.target_shopping_list_id = targetCartId;
      }
      
      const response = await authFetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        if (response.status === 409 && errorData.detail === 'CART_FULFILLED') {
          if (onCartFulfilled) {
            onCartFulfilled();
            onClose();
          } else {
            setError('Shopping cart is fulfilled. Please create a new cart first.');
          }
          return;
        }
        throw new Error(errorData.detail || 'Failed to add items');
      }
      
      const data = await response.json();
      
      if (onItemsConfirmed) {
        onItemsConfirmed(data.items_added || itemsToAdd, {
          type: destinationType,
          id: destinationType === 'pantry' ? targetPantryId : targetCartId,
          name: data.destination_name || getDestinationName()
        });
      }
      
      // Reset for next scan instead of closing
      setSelectedItems([]);
      setAnalysisResult(null);
      setBarcodeInput('');
      setSelectedImage(null);
      setImagePreview(null);
      setSuccessMessage(`✓ Added ${itemsToAdd.length} item(s) to ${getDestinationName()}`);
      
    } catch (err) {
      console.error('Confirm error:', err);
      setError(err.message || 'Failed to add items');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const resetAll = () => {
    setSelectedImage(null);
    setImagePreview(null);
    setAnalysisResult(null);
    setSelectedItems([]);
    setBarcodeInput('');
    setError('');
    setLastScannedCode('');
    scannedBarcodesRef.current.clear();
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    stopScanning();
  };

  const getDestinationName = () => {
    if (destinationType === 'pantry') {
      const pantry = pantries.find(p => p.id === (selectedPantryId || defaultPantryId));
      return pantry?.name || 'Pantry';
    } else {
      const cart = shoppingCarts.find(c => c.id === (selectedCartId || defaultCartId));
      return cart?.name || 'Shopping Cart';
    }
  };

 const hasItems = selectedItems.length > 0;
  // For continuous scanning, don't switch to results until user clicks "Done"
  // (which sets analysisResult). Otherwise hasItems alone triggers the switch.
  const hasResults = analysisResult !== null || (hasItems && inputMethod !== 'continuous');

  // ==========================================================================
  // RENDER HELPERS
  // ==========================================================================

  const renderDestinationSelector = () => {
    if (hideDestinationSelector) return null;
    
    return (
      <div className="vgc-destinationSelector">
        <div className="vgc-destinationTabs">
          <button
            className={`vgc-destinationTab ${destinationType === 'pantry' ? 'vgc-destinationTabActive' : ''}`}
            onClick={() => setDestinationType('pantry')}
          >
            Add to Pantry
          </button>
          <button
            className={`vgc-destinationTab ${destinationType === 'cart' ? 'vgc-destinationTabActive' : ''}`}
            onClick={() => setDestinationType('cart')}
          >
            Add to Shopping Cart
          </button>
        </div>
        
        <div className="vgc-selectWrapper">
          {destinationType === 'pantry' ? (
            <>
              <label className="vgc-selectLabel">Select Pantry</label>
              {pantries.length === 0 ? (
                <div className="vgc-emptyWarning">
                  No pantries found. Create a pantry first.
                </div>
              ) : (
                <select
                  className="vgc-select"
                  value={selectedPantryId || ''}
                  onChange={(e) => setSelectedPantryId(parseInt(e.target.value))}
                >
                  {pantries.map(pantry => (
                    <option key={pantry.id} value={pantry.id}>
                      {pantry.name} {pantry.items?.length > 0 ? `(${pantry.items.length} items)` : ''}
                    </option>
                  ))}
                </select>
              )}
            </>
          ) : (
            <>
              <label className="vgc-selectLabel">Select Shopping Cart</label>
              {shoppingCarts.length === 0 ? (
                <div className="vgc-emptyWarning">
                  No active shopping carts. Create one first.
                </div>
              ) : (
                <select
                  className="vgc-select"
                  value={selectedCartId || ''}
                  onChange={(e) => setSelectedCartId(parseInt(e.target.value))}
                >
                  {shoppingCarts.map(cart => (
                    <option key={cart.id} value={cart.id}>
                      {cart.name} {cart.items_total > 0 ? `(${cart.items_total} items)` : ''}
                    </option>
                  ))}
                </select>
              )}
            </>
          )}
        </div>
      </div>
    );
  };

  const renderInputMethodTabs = () => (
    <div className="vgc-inputMethodTabs">
      {/* Continuous Scan - Mobile Only */}
      {isMobile && (
        <button
          className={`vgc-inputMethodTab ${inputMethod === 'continuous' ? 'vgc-inputMethodTabActive' : ''}`}
          onClick={() => { setInputMethod('continuous'); stopScanning(); }}
        >
          <span className="vgc-tabIcon"></span>
          <span>Scan Barcodes</span>
        </button>
      )}
      
      {/* Photo Scan - All Devices */}
      <button
        className={`vgc-inputMethodTab ${inputMethod === 'photo' ? 'vgc-inputMethodTabActive' : ''}`}
        onClick={() => { setInputMethod('photo'); stopScanning(); }}
      >
        <span className="vgc-tabIcon"></span>
        <span>Photo Scan</span>
      </button>
      
      {/* Manual Entry - All Devices */}
      <button
        className={`vgc-inputMethodTab ${inputMethod === 'enter' ? 'vgc-inputMethodTabActive' : ''}`}
        onClick={() => { setInputMethod('enter'); stopScanning(); }}
      >
        <span className="vgc-tabIcon"></span>
        <span>Enter UPC</span>
      </button>
    </div>
  );

  const renderScanInput = () => (
    <div className="vgc-scannerSection">
      {scannerError ? (
        <div className="vgc-scannerWarning">
          {scannerError}
        </div>
      ) : !isInitialized ? (
        <div className="vgc-scannerLoading">
          Initializing camera scanner...
        </div>
      ) : null}
      
      <div 
        id="strich-scanner-host"
        ref={hostElementRef} 
        className="vgc-scannerHost"
        style={{ display: isScanning ? 'block' : 'none' }}
      />
      
      {!isScanning && selectedItems.length === 0 && (
        <div className="vgc-scannerPlaceholder">
          <div className="vgc-scannerPlaceholderIcon"></div>
          <p className="vgc-scannerPlaceholderTitle">
            Continuous Barcode Scanner
          </p>
          <p className="vgc-scannerPlaceholderText">
            Scan multiple items continuously - they'll appear in the list below
          </p>
        </div>
      )}
      
      {/* Control buttons */}
      <div className="vgc-scannerControls">
        <button
          className={`vgc-scanButton ${isScanning ? 'vgc-scanButtonStop' : ''} ${!isInitialized && !scannerError ? 'vgc-scanButtonDisabled' : ''}`}
          style={{ flex: isScanning || selectedItems.length === 0 ? 1 : 'none' }}
          onClick={isScanning ? stopScanning : startScanning}
          disabled={!isInitialized && !scannerError}
        >
          {isScanning ? (
            <>Stop</>
          ) : (
            <>{selectedItems.length > 0 ? 'Scan More' : 'Start Camera'}</>
          )}
        </button>
        
        {/* Done button - only show when items exist and not scanning */}
        {selectedItems.length > 0 && !isScanning && (
          <button
            className="vgc-scanButton vgc-doneButton"
            onClick={() => {
              setAnalysisResult({ identified_items: selectedItems, source: 'barcode' });
            }}
          >
            ✓ Done ({selectedItems.length})
          </button>
        )}
      </div>
      
      {lastScannedCode && (
        <div className="vgc-lastScanned">
          ✓ Last scanned: {lastScannedCode}
        </div>
      )}
      
      {isLookingUp && (
        <div className="vgc-lookingUpText">
          Looking up product...
        </div>
      )}
      
      {/* Show scanned items inline during continuous scanning */}
      {selectedItems.length > 0 && (
        <div className="vgc-scannedItemsSection">
          <div className="vgc-scannedItemsTitle">
            Scanned Items ({selectedItems.length}):
          </div>
          <div className="vgc-scannedItemsList">
            {selectedItems.map((item, idx) => (
              <div 
                key={idx}
                className="vgc-scannedItemRow"
              >
                <div>
                  <span className="vgc-scannedItemName">{item.name}</span>
                  {item.brand && <span className="vgc-scannedItemBrand"> - {item.brand}</span>}
                </div>
                <button
                  className="vgc-scannedItemRemove"
                  onClick={() => removeItem(idx)}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  const renderEnterInput = () => (
    <div className="vgc-barcodeSection">
      <div className="vgc-enterIcon"></div>
      <p className="vgc-enterTitle">
        Enter Barcode / UPC
      </p>
      
      <div className="vgc-barcodeInputWrapper">
        <input
          type="text"
          value={barcodeInput}
          onChange={(e) => setBarcodeInput(e.target.value)}
          onKeyPress={handleBarcodeKeyPress}
          placeholder="012345678901"
          className="vgc-barcodeInput"
          autoFocus
        />
        <button
          className={`vgc-lookupButton ${isLookingUp || !barcodeInput.trim() ? 'vgc-lookupButtonDisabled' : ''}`}
          onClick={handleManualBarcodeSubmit}
          disabled={isLookingUp || !barcodeInput.trim()}
        >
          {isLookingUp ? 'Looking up...' : 'Look Up'}
        </button>
      </div>
      
      <p className="vgc-enterHint">
        Enter the barcode number from the product packaging
      </p>
      
      {selectedItems.length > 0 && (
        <div className="vgc-itemCount">
          ✓ {selectedItems.length} item(s) added - keep entering or review below
        </div>
      )}
    </div>
  );

  const renderPhotoInput = () => (
    <>
      <div
        className={`vgc-dropZone ${imagePreview ? 'vgc-dropZoneActive' : ''}`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onClick={() => fileInputRef.current?.click()}
      >
        {imagePreview ? (
          <div>
            <img src={imagePreview} alt="Selected" className="vgc-imagePreview" />
            <p className="vgc-changeImageText">Click or drag to change</p>
          </div>
        ) : (
          <div>
            <div className="vgc-photoIcon"></div>
            <p className="vgc-photoTitle">
              Photo Barcode Scan
            </p>
            <p className="vgc-photoHint">
              {isMobile 
                ? 'Upload a photo with visible barcodes • Detects multiple items at once'
                : 'Upload a photo with a visible barcode • 1 Image with 1 Barcode ONLY'
              }
            </p>
          </div>
        )}
      </div>
      
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        className="vgc-hiddenInput"
      />
      
      <div className="vgc-tipsBox">
        <strong>Tips for best results:</strong>
        <ul className="vgc-tipsList">
          {!isMobile && (
            <li><strong>For OPTIMAL/ADDITIONAL FUNCTIONALITY</strong> use your Mobile Device for barcode scanning</li>
          )}
          {isMobile && (
            <>
              <li>Scanning multiple items? Pre-arrange items so all barcodes are visible</li>
              <li>Move from item-to-item - scanning will continue until you click Stop Scanning</li>
            </>
          )}
          <li>If using Photo Scan make sure <strong>barcode is CLEARLY visible</strong> in the photo</li>
          <li>Good lighting helps barcode detection</li>
          <li>Works best with flat/straight barcodes</li>
        </ul>
      </div>
      
      <button
        className={`vgc-analyzeButton ${!imagePreview || isAnalyzing ? 'vgc-analyzeButtonDisabled' : ''}`}
        onClick={analyzeImage}
        disabled={!imagePreview || isAnalyzing}
      >
        {isAnalyzing ? (
          <>
            <span className="vgc-spinner" />
            Analyzing groceries...
          </>
        ) : (
          'Scan for Barcodes'
        )}
      </button>
      
      <p className="vgc-scanHint">
        Detects single barcode in your photo automatically
      </p>
    </>
  );

  const renderItemsList = () => (
    <div className="vgc-itemsList">
      {selectedItems.map((item, idx) => (
        <div 
          key={idx} 
          className={`vgc-itemCard ${item.selected ? 'vgc-itemCardSelected' : 'vgc-itemCardUnselected'}`}
        >
          <input
            type="checkbox"
            checked={item.selected}
            onChange={() => toggleItemSelection(idx)}
            className="vgc-checkbox"
          />
          
          <div className="vgc-itemInfo">
            {item.isManual ? (
              <input
                type="text"
                value={item.name}
                onChange={(e) => updateItemName(idx, e.target.value)}
                placeholder="Enter item name"
                className="vgc-nameInput"
              />
            ) : (
              <div className="vgc-itemName">
                {item.name}
                {item.brand && <span className="vgc-brandLabel"> - {item.brand}</span>}
              </div>
            )}
            
            <div className="vgc-itemCategory">
              {item.category || 'Uncategorized'}
              {item.barcode && <span className="vgc-barcodeLabel">• UPC: {item.barcode}</span>}
            </div>
            
            {item.nutrition && (
              <div className="vgc-itemNutrition">
                {item.nutrition.calories && (
                  <span className="vgc-nutritionBadge">
                    {Math.round(item.nutrition.calories)} cal
                  </span>
                )}
                {item.nutrition.protein && (
                  <span className="vgc-nutritionBadge">
                    {Math.round(item.nutrition.protein)}g protein
                  </span>
                )}
              </div>
            )}
            
            {item.health_labels?.length > 0 && (
              <div className="vgc-itemNutrition">
                {item.health_labels.slice(0, 3).map((label, labelIdx) => (
                  <span 
                    key={labelIdx} 
                    className="vgc-nutritionBadge vgc-healthLabel"
                  >
                    {label.replace(/_/g, ' ')}
                  </span>
                ))}
              </div>
            )}
          </div>
          
          <div className="vgc-quantityControl">
            <input
              type="number"
              value={item.quantity}
              onChange={(e) => updateItemQuantity(idx, parseInt(e.target.value, 10) || 1)}
              min="1"
              step="1"
              className="vgc-quantityInput"
            />
            <span className="vgc-unitLabel">
              {item.unit || 'ea'}
            </span>
          </div>
          
          <button 
            className="vgc-removeButton"
            onClick={() => removeItem(idx)}
          >
            ×
          </button>
        </div>
      ))}
      
      <button 
        className="vgc-addItemButton"
        onClick={addManualItem}
      >
        + Add Item Manually
      </button>
    </div>
  );

  const renderResults = () => {
    const selectedCount = selectedItems.filter(i => i.selected).length;
    
    return (
      <div>
        <div className="vgc-summary">
          <div className="vgc-summaryTitle">
            {selectedItems.length} Item(s) Ready
          </div>
          <div className="vgc-summaryText">
            {selectedCount} selected to add to {getDestinationName()}
          </div>
        </div>
        
        {renderDestinationSelector()}
        
        {imagePreview && (
          <div className="vgc-resultImageWrap">
            <img 
              src={imagePreview} 
              alt="Analyzed groceries" 
              className="vgc-resultImage"
            />
          </div>
        )}
        
        {/* Continue adding section */}
        <div className="vgc-addMoreSection">
          <div className="vgc-addMoreLabel">
            Add more items:
          </div>
          <div className="vgc-barcodeInputWrapper">
            <input
              type="text"
              value={barcodeInput}
              onChange={(e) => setBarcodeInput(e.target.value)}
              onKeyPress={handleBarcodeKeyPress}
              placeholder="Enter another UPC..."
              className="vgc-barcodeInput vgc-addMoreInput"
            />
            <button
              className={`vgc-lookupButton vgc-addMoreButton ${isLookingUp || !barcodeInput.trim() ? 'vgc-lookupButtonDisabled' : ''}`}
              onClick={handleManualBarcodeSubmit}
              disabled={isLookingUp || !barcodeInput.trim()}
            >
              {isLookingUp ? '...' : 'Add'}
            </button>
          </div>
        </div>
        
        {renderItemsList()}
        <EdamamAttribution />
      </div>
    );
  };

  const canSubmit = () => {
    if (selectedItems.filter(i => i.selected).length === 0) return false;
    if (!hideDestinationSelector) {
      if (destinationType === 'pantry' && !selectedPantryId) return false;
      if (destinationType === 'cart' && !selectedCartId) return false;
    }
    return true;
  };

  // ==========================================================================
  // MAIN RENDER
  // ==========================================================================

  return (
    <div className="vgc-overlay">
      <div className="vgc-modal">
        <div className="vgc-header">
          <h3 className="vgc-title">Scan Groceries</h3>
          <div className="vgc-headerActions">
            <button className="vgc-closeButton" onClick={onClose}>×</button>
          </div>
        </div>
        
        <div className="vgc-content">
          {error && (
            <div className="vgc-error">
              {error}
            </div>
          )}

        {successMessage && (
            <div className="vgc-success">
              {successMessage}
            </div>
          )}  
          
          {loadingDestinations ? (
            <div className="vgc-loadingState">
              Loading...
            </div>
          ) : !hasResults ? (
            <>
              {renderDestinationSelector()}
              {renderInputMethodTabs()}
              {inputMethod === 'continuous' && renderScanInput()}
              {inputMethod === 'enter' && renderEnterInput()}
              {inputMethod === 'photo' && renderPhotoInput()}
            </>
          ) : (
            renderResults()
          )}
        </div>
        
        <div className="vgc-footer">
          {hasResults ? (
            <>
              <button className="vgc-secondaryButton" onClick={resetAll}>
                ← Start Over
              </button>
              <button 
                className={`vgc-primaryButton ${!canSubmit() || isSubmitting ? 'vgc-primaryButtonDisabled' : ''}`}
                onClick={confirmItems}
                disabled={!canSubmit() || isSubmitting}
              >
                {isSubmitting ? 'Adding...' : (
                  <>
                    Add {selectedItems.filter(i => i.selected).length} Items
                  </>
                )}
              </button>
            </>
          ) : (
            <button 
              className="vgc-secondaryButton vgc-cancelButton"
              onClick={onClose}
            >
              Cancel
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default VisionGroceryCapture;